import { easeInOut, motion } from 'motion/react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '../Button/Button';
import { Form, FormControl, FormField, FormItem, useForm } from '../ui/Form';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../ui/InputOtp';

const emailInputVariants = {
  hidden: { opacity: 0, height: 0, marginBottom: 0 },
  visible: {
    opacity: 1,
    height: 'auto',
    transition: { ease: easeInOut, duration: 0.2 },
  },
};

interface OtpFormProps {
  callbackUrl: string;
  email: string;
}

const OtpForm = ({ callbackUrl, email }: OtpFormProps) => {
  const form = useForm({
    schema: z.object({
      token: z.string(),
    }),
  });

  const onSubmit = async ({ token }: { token: string }) => {
    const url = new URL('/api/auth/callback/resend', window.location.href);
    url.searchParams.append('email', email);
    url.searchParams.append('token', token);
    url.searchParams.append('callbackUrl', callbackUrl);

    const response = await fetch(url.href);
    const responseUrl = new URL(response.url, window.location.href); // Final url with all redirects
    const errorSearchParam = responseUrl.searchParams.get('error');

    if (!response.ok) {
      toast.error('Code might be invalid or expired.');
      return;
    }
    if (errorSearchParam === null) {
      window.location.href = `${window.location.origin}${callbackUrl}`;
      return;
    }
    toast.error('Code might be invalid or expired.');
  };

  return (
    <Form {...form}>
      <motion.form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-3 mb-2"
        initial="hidden"
        animate="visible"
        variants={emailInputVariants}
      >
        <FormField
          control={form.control}
          name="token"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <InputOTP maxLength={6} {...field}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
            </FormItem>
          )}
        />

        <Button
          isLoading={form.formState.isSubmitting}
          size="lg"
          className="hover:scale-[1.02] transition-al"
          type="submit"
        >
          {form.formState.isSubmitting ? 'Signing in' : 'Continue'}
        </Button>

        <div className="flex flex-row justify-stretch gap-2">
          <div className="w-1/2">
            <Button
              type="button"
              size="lg"
              className="w-full hover:scale-[1.02] "
              variant="secondary"
              onClick={() => {
                const gmailUrl = 'https://mail.google.com';
                const newWindow = window.open(gmailUrl, '_blank');
                if (
                  !newWindow ||
                  newWindow.closed ||
                  typeof newWindow.closed === 'undefined'
                ) {
                  window.location.href = gmailUrl;
                }
              }}
            >
              Open Gmail
            </Button>
          </div>
          <div className="w-1/2">
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="w-full hover:scale-[1.02] "
              onClick={() => {
                const outlookUrl = 'https://outlook.live.com';
                const newWindow = window.open(outlookUrl, '_blank');
                if (
                  !newWindow ||
                  newWindow.closed ||
                  typeof newWindow.closed === 'undefined'
                ) {
                  window.location.href = outlookUrl;
                }
              }}
            >
              Open Outlook
            </Button>
          </div>
        </div>
      </motion.form>
    </Form>
  );
};

export default OtpForm;
